import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';
import { toast } from 'sonner';
import Navbar from '../components/Navbar';
import LoadingSkeleton from '../components/LoadingSkeleton';
import { ArrowLeft, ChevronDown, ChevronRight, Calendar, Check, X, Monitor, MapPin, Phone } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const ADMIN_EMAILS = ["julie@jcp-teaching.com", "test@example.com", "jc-p@outlook.de"];

const AdminBookingsPage = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { user, token, loading: authLoading } = useAuth();
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(false);
  const [expandedMonths, setExpandedMonths] = useState({});
  const [processingPayment, setProcessingPayment] = useState({});

  useEffect(() => {
    if (!authLoading && !user) {
      navigate('/login');
      return;
    }
    
    if (user && !ADMIN_EMAILS.includes(user.email)) {
      toast.error('Admin access required');
      navigate('/');
    }
  }, [user, authLoading, navigate]);

  useEffect(() => {
    if (user && ADMIN_EMAILS.includes(user.email)) {
      fetchAllBookings();
    }
  }, [user]);

  const fetchAllBookings = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/admin/bookings/all`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setBookings(response.data);
      
      // Auto-expand current and next month
      const now = new Date();
      const currentMonthKey = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
      const nextMonth = new Date(now.getFullYear(), now.getMonth() + 1, 1);
      const nextMonthKey = `${nextMonth.getFullYear()}-${String(nextMonth.getMonth() + 1).padStart(2, '0')}`;
      
      setExpandedMonths({ [currentMonthKey]: true, [nextMonthKey]: true });
    } catch (error) {
      console.error('Error fetching bookings:', error);
      toast.error('Failed to load bookings');
    } finally {
      setLoading(false);
    }
  };

  const handleMarkPaid = async (bookingId, bookingType) => {
    const isGreven = bookingType === 'greven' || bookingType === 'lesson';
    const confirmMsg = isGreven 
      ? t('Mark as paid? (Heiden booking will be deleted after payment)')
      : t('Mark as paid?');
    
    if (!window.confirm(confirmMsg)) {
      return;
    }

    setProcessingPayment(prev => ({ ...prev, [bookingId]: true }));

    try {
      const response = await axios.post(
        `${API}/admin/bookings/${bookingId}/mark-paid`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      if (response.data.deleted) {
        toast.success(t('Booking marked as paid and deleted'));
      } else {
        toast.success(t('Booking marked as paid'));
      }
      
      fetchAllBookings();
    } catch (error) {
      console.error('Error marking paid:', error);
      toast.error(error.response?.data?.detail || t('Could not update payment'));
    } finally {
      setProcessingPayment(prev => ({ ...prev, [bookingId]: false }));
    }
  };

  const handleCancelBooking = async (bookingId) => {
    if (!window.confirm(t('Are you sure you want to cancel this booking?'))) {
      return;
    }

    try {
      await axios.delete(`${API}/admin/bookings/${bookingId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(t('Booking cancelled'));
      fetchAllBookings();
    } catch (error) {
      console.error('Error cancelling booking:', error);
      toast.error(t('Could not cancel booking'));
    }
  };

  const toggleMonth = (monthKey) => {
    setExpandedMonths(prev => ({
      ...prev,
      [monthKey]: !prev[monthKey]
    }));
  };

  // Group bookings by month
  const groupByMonth = (bookings) => {
    const grouped = {};
    
    bookings.forEach(booking => {
      const date = new Date(booking.date + 'T00:00:00');
      const monthKey = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
      
      if (!grouped[monthKey]) {
        grouped[monthKey] = {
          label: date.toLocaleDateString('da-DK', { year: 'numeric', month: 'long' }),
          items: []
        };
      }
      grouped[monthKey].items.push(booking);
    });
    
    // Sort months chronologically
    const sortedKeys = Object.keys(grouped).sort();
    const sorted = {};
    sortedKeys.forEach(key => {
      // Sort items within each month by date (earliest first)
      grouped[key].items.sort((a, b) => {
        const dateA = new Date(a.date + 'T' + (a.time_slot || '00:00'));
        const dateB = new Date(b.date + 'T' + (b.time_slot || '00:00'));
        return dateA - dateB;
      });
      sorted[key] = grouped[key];
    });
    
    return sorted;
  };

  const getBookingTypeLabel = (type) => {
    switch(type) {
      case 'online': return t('Pivo Online');
      case 'greven': return t('Live in Heiden');
      case 'lesson': return t('Live in Heiden');
      default: return type;
    }
  };

  const getBookingTypeIcon = (type) => {
    if (type === 'online') {
      return <Monitor size={14} />;
    }
    return <MapPin size={14} />;
  };

  if (authLoading || !user) {
    return (
      <div>
        <Navbar />
        <div style={{ padding: '2rem', maxWidth: '1200px', margin: '0 auto' }}>
          <LoadingSkeleton type="card" count={3} />
        </div>
      </div>
    );
  }

  if (!ADMIN_EMAILS.includes(user.email)) {
    return null;
  }

  const groupedBookings = groupByMonth(bookings);
  const totalBookings = bookings.length;
  const pendingCount = bookings.filter(b => b.payment_status !== 'paid').length;

  return (
    <div>
      <Navbar />
      <div className="booking-page" style={{ paddingTop: '100px', minHeight: '100vh', background: '#fafafa' }}>
        <div className="admin-container" style={{ maxWidth: '1000px', margin: '0 auto', padding: '2rem' }}>
          {/* Header with back button */}
          <div style={{ 
            background: 'white', 
            padding: '2rem', 
            borderRadius: '12px', 
            marginBottom: '2rem',
            boxShadow: '0 2px 10px rgba(0,0,0,0.05)'
          }}>
            <button
              onClick={() => navigate('/admin')}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '0.5rem',
                background: 'none',
                border: 'none',
                color: '#7c6f5e',
                cursor: 'pointer',
                fontSize: '0.95rem',
                marginBottom: '1rem',
                padding: '0.5rem',
                borderRadius: '5px',
                transition: 'background 0.2s'
              }}
              onMouseOver={(e) => e.currentTarget.style.background = '#f5f5f5'}
              onMouseOut={(e) => e.currentTarget.style.background = 'none'}
            >
              <ArrowLeft size={20} />
              {t('Back to Admin Dashboard')}
            </button>
            <h1 className="booking-title" style={{ marginBottom: '0.5rem', fontSize: '2rem' }}>
              {t('Manage Bookings')}
            </h1>
            <p style={{ color: '#666', marginBottom: '0', fontSize: '1rem' }}>
              {totalBookings} {t('bookings')} {t('total')} • {pendingCount} {t('awaiting payment')}
            </p>
          </div>

          {/* Bookings Content - Grouped by Month */}
          {loading ? (
            <LoadingSkeleton type="card" count={3} />
          ) : bookings.length === 0 ? (
            <div style={{
              background: 'white',
              padding: '3rem',
              borderRadius: '12px',
              boxShadow: '0 2px 10px rgba(0,0,0,0.05)',
              textAlign: 'center'
            }}>
              <Calendar size={48} color="#ccc" style={{ marginBottom: '1rem' }} />
              <p style={{ color: '#666' }}>{t('No bookings yet.')}</p>
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
              {Object.entries(groupedBookings).map(([monthKey, monthData]) => (
                <div 
                  key={monthKey}
                  style={{
                    background: 'white',
                    borderRadius: '12px',
                    boxShadow: '0 2px 10px rgba(0,0,0,0.05)',
                    overflow: 'hidden'
                  }}
                >
                  {/* Month Header - Clickable */}
                  <button
                    onClick={() => toggleMonth(monthKey)}
                    style={{
                      width: '100%',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      padding: '1.25rem 1.5rem',
                      background: expandedMonths[monthKey] ? '#7c6f5e' : '#f8f8f8',
                      color: expandedMonths[monthKey] ? 'white' : '#333',
                      border: 'none',
                      cursor: 'pointer',
                      transition: 'all 0.2s ease'
                    }}
                  >
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                      {expandedMonths[monthKey] ? (
                        <ChevronDown size={20} />
                      ) : (
                        <ChevronRight size={20} />
                      )}
                      <span style={{ 
                        fontSize: '1.1rem', 
                        fontWeight: 600,
                        textTransform: 'capitalize'
                      }}>
                        {monthData.label}
                      </span>
                    </div>
                    <span style={{ 
                      background: expandedMonths[monthKey] ? 'rgba(255,255,255,0.2)' : '#e0e0e0',
                      padding: '0.25rem 0.75rem',
                      borderRadius: '20px',
                      fontSize: '0.85rem'
                    }}>
                      {monthData.items.length} {monthData.items.length !== 1 ? t('bookings') : t('booking')}
                    </span>
                  </button>

                  {/* Month Content - Expandable */}
                  {expandedMonths[monthKey] && (
                    <div style={{ padding: '0.75rem' }}>
                      {monthData.items.map((booking) => {
                        const dateObj = new Date(booking.date + 'T00:00:00');
                        const isPending = booking.payment_status !== 'paid';
                        const isOnline = booking.booking_type === 'online';
                        const isProcessing = processingPayment[booking.id];
                        
                        return (
                          <div
                            key={booking.id}
                            style={{
                              display: 'flex',
                              flexDirection: 'column',
                              padding: '0.875rem',
                              background: '#fafafa',
                              borderRadius: '8px',
                              marginBottom: '0.5rem',
                              borderLeft: `4px solid ${isOnline ? '#2196f3' : '#7c6f5e'}`
                            }}
                          >
                            {/* Top row: Date, Time, Type badge */}
                            <div style={{ 
                              display: 'flex', 
                              justifyContent: 'space-between', 
                              alignItems: 'center',
                              marginBottom: '0.5rem',
                              flexWrap: 'wrap',
                              gap: '0.5rem'
                            }}>
                              <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                                <span style={{ fontWeight: 700, color: '#333', fontSize: '1rem' }}>
                                  {dateObj.toLocaleDateString('da-DK', {
                                    weekday: 'short',
                                    day: 'numeric',
                                    month: 'short'
                                  })}
                                </span>
                                <span style={{ 
                                  color: '#555', 
                                  fontWeight: 600,
                                  background: '#fff',
                                  padding: '0.15rem 0.5rem',
                                  borderRadius: '4px',
                                  fontSize: '0.9rem'
                                }}>
                                  {booking.start_time}
                                </span>
                              </div>
                              <span style={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: '0.25rem',
                                padding: '0.25rem 0.6rem',
                                background: isOnline ? '#e3f2fd' : '#e8f5e9',
                                color: isOnline ? '#1976d2' : '#2e7d32',
                                borderRadius: '12px',
                                fontSize: '0.75rem',
                                fontWeight: 600
                              }}>
                                {getBookingTypeIcon(booking.booking_type)}
                                {isOnline ? 'Online' : 'Heiden'}
                              </span>
                            </div>
                            
                            {/* Client info row */}
                            <div style={{ 
                              display: 'flex', 
                              justifyContent: 'space-between', 
                              alignItems: 'center',
                              flexWrap: 'wrap',
                              gap: '0.5rem'
                            }}>
                              <div>
                                <div style={{ fontWeight: 600, color: '#333', fontSize: '0.95rem' }}>
                                  {booking.client_name}
                                </div>
                                {booking.client_phone && (
                                  <div style={{ 
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '0.5rem',
                                    marginTop: '0.25rem'
                                  }}>
                                    <a 
                                      href={`tel:${booking.client_phone}`}
                                      style={{ 
                                        display: 'inline-flex',
                                        alignItems: 'center',
                                        gap: '0.25rem',
                                        color: '#1976d2',
                                        fontSize: '0.85rem',
                                        textDecoration: 'none'
                                      }}
                                    >
                                      <Phone size={14} />
                                      {booking.client_phone}
                                    </a>
                                    <a 
                                      href={`https://wa.me/${booking.client_phone.replace(/[^0-9]/g, '')}`}
                                      target="_blank"
                                      rel="noopener noreferrer"
                                      style={{ 
                                        display: 'inline-flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        padding: '0.25rem 0.5rem',
                                        background: '#25D366',
                                        color: 'white',
                                        fontSize: '0.75rem',
                                        fontWeight: 600,
                                        borderRadius: '4px',
                                        textDecoration: 'none'
                                      }}
                                    >
                                      WhatsApp
                                    </a>
                                  </div>
                                )}
                              </div>
                              
                              {/* Payment Status / Actions */}
                              <div style={{ display: 'flex', alignItems: 'center', gap: '0.4rem' }}>
                                {isPending ? (
                                  <button
                                    onClick={() => handleMarkPaid(booking.id, booking.booking_type)}
                                    disabled={isProcessing}
                                    style={{
                                      display: 'flex',
                                      alignItems: 'center',
                                      gap: '0.3rem',
                                      padding: '0.4rem 0.75rem',
                                      background: isProcessing ? '#ccc' : '#ff9800',
                                      color: 'white',
                                      border: 'none',
                                      borderRadius: '6px',
                                      cursor: isProcessing ? 'wait' : 'pointer',
                                      fontSize: '0.8rem',
                                      fontWeight: 500
                                    }}
                                  >
                                    <Check size={14} />
                                    {isProcessing ? '...' : 'Betalt'}
                                  </button>
                                ) : (
                                  <span style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '0.25rem',
                                    padding: '0.35rem 0.6rem',
                                    background: '#c8e6c9',
                                    color: '#2e7d32',
                                    borderRadius: '6px',
                                    fontSize: '0.75rem',
                                    fontWeight: 600
                                  }}>
                                    <Check size={12} />
                                    Betalt
                                  </span>
                                )}
                                
                                {isPending && (
                                  <button
                                    onClick={() => handleCancelBooking(booking.id)}
                                    title="Slet booking"
                                    style={{
                                      display: 'flex',
                                      alignItems: 'center',
                                      padding: '0.4rem',
                                      background: '#ffebee',
                                      color: '#c62828',
                                      border: 'none',
                                      borderRadius: '6px',
                                      cursor: 'pointer'
                                    }}
                                  >
                                    <X size={16} />
                                  </button>
                                )}
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default AdminBookingsPage;
